import {
  Button,
  ButtonGroup,
  Choice,
  closeCurrentDialog,
  DataStatus,
  DialogFooter,
  Form,
  FormButton,
  FormSubmitHandler,
  Radio,
  Separator,
  TextInput,
} from 'platform/components';
import {Grid, GridItem, VStack} from 'platform/foundation';
import {DeepPartial} from 'utility-types';

import {defaultTo} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetManufacturerQuery, useGetManufacturersQuery} from '@dms/api/core';
import {
  PatchReceiveNoteCorrectionItemRequest,
  useGetHandlingUnitQuery,
  useGetHandlingUnitsQuery,
  useGetReceiveNoteCorrectionItemQuery,
  useGetSuppliersQuery,
  useGetWarehousesQuery,
  usePatchReceiveNoteCorrectionItemMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  getOptionsFromManufacturers,
  getOptionsFromWarehouses,
  handleApiError,
  usePermissions,
} from '@dms/shared';
import {FALLBACK_HANDLING_UNIT} from '@dms/warehouse/shared';

import {generateHashFromObjects, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  ReceiveNoteCorrectionItemForm,
  ReceiveNoteCorrectionItemFormSchema,
} from '../types/ReceiveNoteCorrectionItemForm';
import {ArticleDetailCard} from './ArticleDetailCard';
import {DeliveryNoteCorrectionItemDetailCard} from './DeliveryNoteCorrectionItemDetailCard';
import {DeliveryNoteItemForm} from './DeliveryNoteItemForm';
import {useReceiveNoteCorrectionItemFormRecalculation} from './hooks/useReceiveNoteCorrectionItemFormRecalculation';
import {ReceiveNoteItemForm} from './ReceiveNoteItemForm';
import {SupplierArticleForm} from './SupplierArticleForm';

interface ReceiveNoteCorrectionItemEditProps extends RequiredTestIdProps {
  receiveNoteCorrectionId: string;
  receiveNoteCorrectionItemId: string;
  isReceiveNoteCorrectionCompleted: boolean;
  onAfterSubmit: VoidFunction;
}

export function ReceiveNoteCorrectionItemEdit(props: ReceiveNoteCorrectionItemEditProps) {
  const [receiveNoteCorrectionEdit] = usePermissions({
    permissionKeys: ['receiveNoteCorrectionEdit'],
  });

  const {
    data: receiveNoteCorrectionItem,
    isLoading: isReceiveNoteCorrectionItemLoading,
    isError: hasReceiveNoteCorrectionItemError,
  } = useGetReceiveNoteCorrectionItemQuery({
    creditNoteId: props.receiveNoteCorrectionId,
    creditNoteItemId: props.receiveNoteCorrectionItemId,
  });

  const {
    data: manufacturers,
    isLoading: isLoadingManufacturers,
    isError: hasManufacturersError,
  } = useGetManufacturersQuery();

  const {
    data: warehouses,
    isLoading: isLoadingWarehouses,
    isError: hasWarehousesError,
  } = useGetWarehousesQuery();

  const {
    data: suppliers,
    isLoading: areSuppliersLoading,
    isError: hasSuppliersError,
  } = useGetSuppliersQuery();

  const {
    data: handlingUnits,
    isLoading: areHandlingUnitsLoading,
    isError: hasHandlingUnitsError,
  } = useGetHandlingUnitsQuery();

  const {
    data: receiveNoteCorrectionItemHandlingUnit,
    isLoading: isReceiveNoteCorrectionItemHandlingUnitLoading,
    isError: hasReceiveNoteCorrectionItemHandlingUnitError,
  } = useGetHandlingUnitQuery(
    {id: receiveNoteCorrectionItem?.article?.handlingUnit as string},
    {skip: isNilOrEmpty(receiveNoteCorrectionItem?.article?.handlingUnit)}
  );

  const {
    data: deliveryNoteItemHandlingUnit,
    isLoading: isDeliveryNoteItemHandlingUnitLoading,
    isError: hasDeliveryNoteItemHandlingUnitError,
  } = useGetHandlingUnitQuery(
    {id: receiveNoteCorrectionItem?.supplierArticle?.supplierUnitId as string},
    {skip: isNilOrEmpty(receiveNoteCorrectionItem?.supplierArticle?.supplierUnitId)}
  );

  const {
    data: manufacturerByReceiveNoteItemCorrection,
    isLoading: isManufacturerByReceiveNoteItemCorrectionLoading,
    isError: hasManufacturerByReceiveNoteItemCorrectionError,
  } = useGetManufacturerQuery(
    {manufacturerId: receiveNoteCorrectionItem?.article?.manufacturerId as string},
    {skip: isNilOrEmpty(receiveNoteCorrectionItem?.article?.manufacturerId)}
  );

  const [patchReceiveNoteCorrectionItem] = usePatchReceiveNoteCorrectionItemMutation({
    fixedCacheKey: 'edit-receive-note-correction-item',
  });

  const isLoading =
    isReceiveNoteCorrectionItemLoading ||
    isReceiveNoteCorrectionItemHandlingUnitLoading ||
    areSuppliersLoading ||
    isDeliveryNoteItemHandlingUnitLoading ||
    isManufacturerByReceiveNoteItemCorrectionLoading;

  const hasError =
    hasReceiveNoteCorrectionItemError ||
    hasSuppliersError ||
    hasHandlingUnitsError ||
    hasReceiveNoteCorrectionItemHandlingUnitError ||
    hasDeliveryNoteItemHandlingUnitError ||
    hasManufacturerByReceiveNoteItemCorrectionError ||
    hasManufacturersError ||
    hasWarehousesError;

  const manufacturerNumber = defaultTo(
    null,
    receiveNoteCorrectionItem?.article?.manufacturerNumber
  );

  const manufacturerId = defaultTo(null, receiveNoteCorrectionItem?.article?.manufacturerId);

  const warehouseId = defaultTo(null, receiveNoteCorrectionItem?.article?.warehouseId);

  const defaultValues: DeepPartial<ReceiveNoteCorrectionItemForm> = {
    correctionType: receiveNoteCorrectionItem?.correctionType,
    receiveItem: receiveNoteCorrectionItem?.receiveItem,
    deliveryItem: receiveNoteCorrectionItem?.deliveryItem,
    supplierArticle: {
      supplierQuantity: receiveNoteCorrectionItem?.supplierArticle?.supplierQuantity,
      supplierUnitId: receiveNoteCorrectionItem?.supplierArticle?.supplierUnitId,
      warehouseQuantity: receiveNoteCorrectionItem?.supplierArticle?.warehouseQuantity,
      warehouseUnitId: receiveNoteCorrectionItem?.article?.handlingUnit,
      supplierOrderingNumber: receiveNoteCorrectionItem?.supplierArticle?.supplierOrderingNumber,
      supplierBulkPackageQuantity:
        receiveNoteCorrectionItem?.supplierArticle?.supplierBulkPackageQuantity,
      supplier: suppliers?.find(
        (supplier) => supplier.id === receiveNoteCorrectionItem?.supplierArticle?.supplierId
      )?.name,
    },
  };

  const {handleChange, isCalculateLoading} = useReceiveNoteCorrectionItemFormRecalculation({
    receiveNoteCorrectionItemId: props.receiveNoteCorrectionItemId,
  });

  const receiveUnit = defaultTo(
    FALLBACK_HANDLING_UNIT,
    receiveNoteCorrectionItemHandlingUnit?.name
  );

  const deliveryUnit = defaultTo(FALLBACK_HANDLING_UNIT, deliveryNoteItemHandlingUnit?.name);

  const handleSubmit: FormSubmitHandler<ReceiveNoteCorrectionItemForm> = async (formValues) => {
    const requestBody: PatchReceiveNoteCorrectionItemRequest['body'] = {
      receiveItem: {
        receiveCorrectionQuantity: formValues.receiveItem.receiveCorrectionQuantity,
        receiveCorrectionUnitPrice: formValues.receiveItem.receiveCorrectionUnitPrice,
        receiveCorrectionStockValue: formValues.receiveItem.receiveCorrectionStockValue,
        receiveAfterQuantity: formValues.receiveItem.receiveAfterQuantity,
        receiveAfterUnitPrice: formValues.receiveItem.receiveAfterUnitPrice,
        receiveAfterStockValue: formValues.receiveItem.receiveAfterStockValue,
      },
      deliveryItem: {
        deliveryCorrectionQuantity: formValues.deliveryItem.deliveryCorrectionQuantity,
        deliveryCorrectionUnitPrice: formValues.deliveryItem.deliveryCorrectionUnitPrice,
        deliveryCorrectionStockValue: formValues.deliveryItem.deliveryCorrectionStockValue,
        deliveryAfterQuantity: formValues.deliveryItem.deliveryAfterQuantity,
        deliveryAfterUnitPrice: formValues.deliveryItem.deliveryAfterUnitPrice,
        deliveryAfterStockValue: formValues.deliveryItem.deliveryAfterStockValue,
      },
    };

    await patchReceiveNoteCorrectionItem({
      creditNoteItemId: props.receiveNoteCorrectionItemId,
      body: requestBody,
    })
      .unwrap()
      .then(closeCurrentDialog)
      .then(props.onAfterSubmit)
      .catch(handleApiError);
  };

  const isNotCurrentCorrectionType = (option: string) =>
    receiveNoteCorrectionItem?.correctionType !== option;

  return (
    <Form<ReceiveNoteCorrectionItemForm>
      key={generateHashFromObjects(receiveNoteCorrectionItem)}
      experimentalZodSchema={ReceiveNoteCorrectionItemFormSchema}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      isDisabled={!receiveNoteCorrectionEdit}
    >
      {(control, formApi) => {
        // For controlling recalculations of prices
        formApi.watch((data, change) => handleChange(data, change, formApi));

        return (
          <DataStatus isLoading={isLoading} isError={hasError} minHeight={100}>
            <VStack data-testid={props['data-testid']}>
              <VStack spacing={4}>
                <Grid columns={4} spacing={4} align="center">
                  <GridItem span={2}>
                    <TextInput
                      isRequired
                      isDisabled
                      value={manufacturerNumber}
                      label={i18n.t('entity.warehouse.labels.catalogueNumber')}
                      data-testid={suffixTestId('inputs.manufacturerNumber', props)}
                    />
                  </GridItem>
                  <GridItem span={1}>
                    <Choice
                      isRequired
                      isDisabled
                      isLoading={isLoadingManufacturers}
                      value={manufacturerId}
                      label={i18n.t('entity.warehouse.labels.manufacturer')}
                      options={getOptionsFromManufacturers(manufacturers)}
                      data-testid={suffixTestId('inputs.manufacturerId', props)}
                    />
                  </GridItem>
                  <GridItem span={1}>
                    <Choice
                      isDisabled
                      isLoading={isLoadingWarehouses}
                      value={warehouseId}
                      label={i18n.t('entity.warehouse.labels.warehouse')}
                      options={getOptionsFromWarehouses(warehouses)}
                      data-testid={suffixTestId('inputs.warehouseId', props)}
                    />
                  </GridItem>
                  <GridItem span={2}>
                    <Radio
                      label={i18n.t('entity.warehouse.labels.correctionType')}
                      value={receiveNoteCorrectionItem?.correctionType ?? null}
                      options={[
                        {
                          label: i18n.t('entity.warehouse.labels.return'),
                          value: RETURN_TO_SUPPLIER_OPTION,
                          isDisabled: isNotCurrentCorrectionType(RETURN_TO_SUPPLIER_OPTION),
                        },
                        {
                          label: i18n.t('entity.warehouse.labels.valueChange'),
                          value: VALUE_CORRECTION_OPTION,
                          isDisabled: isNotCurrentCorrectionType(VALUE_CORRECTION_OPTION),
                        },
                        {
                          label: i18n.t('entity.warehouse.labels.combinedCorrection'),
                          value: RETURN_AND_PRICE_CHANGE_OPTION,
                          isDisabled: isNotCurrentCorrectionType(RETURN_AND_PRICE_CHANGE_OPTION),
                        },
                      ]}
                      data-testid={suffixTestId('inputs.correctionType', props)}
                    />
                  </GridItem>
                </Grid>
              </VStack>

              <Separator />

              <VStack spacing={4}>
                <ReceiveNoteItemForm
                  control={control}
                  receiveUnit={receiveUnit}
                  currency={receiveNoteCorrectionItem?.currency}
                  isReceiveNoteCorrectionCompleted={props.isReceiveNoteCorrectionCompleted}
                  data-testid={suffixTestId('forms.receiveNoteItem', props)}
                />
                <Separator spacing={0} />
                <DeliveryNoteItemForm
                  control={control}
                  deliveryUnit={deliveryUnit}
                  currency={receiveNoteCorrectionItem?.currency}
                  isReceiveNoteCorrectionCompleted={props.isReceiveNoteCorrectionCompleted}
                  data-testid={suffixTestId('forms.deliveryNoteItem', props)}
                />
                <Separator spacing={0} />
                <SupplierArticleForm
                  control={control}
                  handlingUnits={handlingUnits}
                  areHandlingUnitsLoading={areHandlingUnitsLoading}
                  data-testid={suffixTestId('forms.supplierArticle', props)}
                />
                <DeliveryNoteCorrectionItemDetailCard
                  deliveryNoteCorrectionItemDetails={{
                    manufacturerNumber: receiveNoteCorrectionItem?.article?.manufacturerNumber,
                    manufacturerName: manufacturerByReceiveNoteItemCorrection?.name,
                    supplierOrderingNumber:
                      receiveNoteCorrectionItem?.supplierArticle?.supplierOrderingNumber,
                    articleName: receiveNoteCorrectionItem?.article?.name,
                    articleDescription: receiveNoteCorrectionItem?.article?.description,
                  }}
                  data-testid={suffixTestId('cards.deliveryNoteCorrectionItemDetail', props)}
                />
                <ArticleDetailCard
                  warehouseId={receiveNoteCorrectionItem?.article?.warehouseId}
                  articleId={receiveNoteCorrectionItem?.article?.id}
                  data-testid={suffixTestId('cards.articleDetail', props)}
                />

                <DialogFooter>
                  <ButtonGroup align="right">
                    <Button
                      variant="secondary"
                      title={i18n.t('general.actions.discard')}
                      onClick={closeCurrentDialog}
                      data-testid={suffixTestId('actions.discard', props)}
                    />
                    <FormButton
                      control={control}
                      type="submit"
                      variant="primary"
                      title={i18n.t('general.actions.saveChanges')}
                      isLoading={isCalculateLoading}
                      data-testid={suffixTestId('actions.submit', props)}
                    />
                  </ButtonGroup>
                </DialogFooter>
              </VStack>
            </VStack>
          </DataStatus>
        );
      }}
    </Form>
  );
}

const RETURN_TO_SUPPLIER_OPTION = 'RETURN_TO_SUPPLIER';
const VALUE_CORRECTION_OPTION = 'VALUE_CORRECTION';
const RETURN_AND_PRICE_CHANGE_OPTION = 'RETURN_AND_PRICE_CHANGE';
